<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import * as jwt from 'jsonwebtoken'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { loginWithPasskey } from '@/services/auth/login-passkey-native'
import {
  mintSessionTransferToken,
  buildSessionTransferAuthorizeUrl,
  exchangeCodeForTokens,
} from '@/services/auth/session-transfer'
import { getUserToken } from '@/services/auth/check-session'

const { useState: useLoginDomainStoreState, useGetters: useLoginDomainStoreGetters } = createNamespacedHelpers('LoginDomain')

/**
  POC harness — the whole native-passkey login path, end to end:

    passkey login (webauthn grant)         -> refresh_token
    refresh_token                          -> session_transfer_token
    /authorize?session_transfer_token=...  -> Auth0 session + cookie + CODE
    code                                   -> id_token WITH sid and custom claims

  Two return modes:

    inspect  comes back here, exchanges the code itself, and prints everything that
             matters — sid, the four siteminder claims, what authenticationMethods
             reports — plus a live check that the Auth0 cookie is now usable.

    real     comes back to /authenticate and lets the actual pipeline run, proving the
             flow works as a login rather than only as a token dance.

  Inspect mode needs `/login2` in the client's allowed callbacks.
* */
export default defineComponent({

  name: 'LoginPage2',

  components: { DefaultLayout },

  setup() {

    const { tenant } = useLoginDomainStoreState(['tenant'])
    const { logo } = useLoginDomainStoreGetters(['logo'])

    const loading = ref(false)
    const errorMessage = ref('')
    const status = ref('')

    // Populated when /authorize sends us back in inspect mode.
    const readout = ref<{ label: string, value: string, good: boolean }[] | null>(null)
    const authMethods = ref('')
    const cookieWorks = ref<boolean | null>(null)

    const origin = `${window.location.protocol}//${window.location.host}`

    /**
      Diagnostic: the webauthn grant ONLY — no session transfer, no navigation.

      We inferred "the grant creates no Auth0 session" from the id_token having no `sid`,
      which does not actually follow: Auth0 could create a session and simply omit the
      claim. Run this, then list the user's sessions in the Management API
      (GET /api/v2/users/{auth0_id}/sessions) to see whether a session exists and what
      `authentication.methods` it records. Delete existing sessions first or the result
      is ambiguous.
    * */
    const runPasskeyOnly = async () => {

      errorMessage.value = ''
      status.value = ''
      readout.value = null
      loading.value = true

      try {

        const tokens = await loginWithPasskey(tenant.value)

        status.value = `Grant done. refresh_token: ${tokens?.refresh_token ? 'yes' : 'no'}`
          + ' — now list the user\'s sessions in the Management API. Nothing was navigated.'

      } catch (error: any) {

        errorMessage.value = error?.name === 'NotAllowedError'
          ? 'Cancelled, or no passkey on this device matched the tenant rpId'
          : (error?.response?.data?.error_description || error?.message || 'Grant failed')

        // eslint-disable-next-line no-console
        console.error('[session-transfer-poc] grant-only failed', error, error?.response?.data)

      } finally {
        loading.value = false
      }

    }

    const runFlow = async (mode: 'inspect' | 'real') => {

      errorMessage.value = ''
      status.value = ''
      readout.value = null
      loading.value = true

      try {

        status.value = 'Running passkey ceremony…'
        const tokens = await loginWithPasskey(tenant.value)

        if (!tokens?.refresh_token) {
          throw new Error(
            'No refresh_token from the webauthn grant. The token request must send '
              + 'offline_access and NO audience — see login-passkey-native.ts.',
          )
        }

        status.value = 'Minting session transfer token…'
        const sessionTransferToken = await mintSessionTransferToken(tenant.value, tokens.refresh_token)

        if (!sessionTransferToken) {
          throw new Error('Mint returned no access_token, i.e. no session transfer token')
        }

        // Single use and ~60s: navigate immediately, nothing between mint and assign.
        const redirectUri = mode === 'inspect' ? `${origin}/login2` : `${origin}/authenticate`

        window.location.assign(buildSessionTransferAuthorizeUrl(tenant.value, {
          sessionTransferToken,
          redirectUri,
        }))

      } catch (error: any) {

        errorMessage.value = error?.name === 'NotAllowedError'
          ? 'Cancelled, or no passkey on this device matched the tenant rpId'
          : (error?.response?.data?.error_description
            || error?.response?.data?.error
            || error?.message
            || 'Flow failed')

        // eslint-disable-next-line no-console
        console.error('[session-transfer-poc] failed', error, error?.response?.data)

        loading.value = false

      }

    }

    const inspectReturn = async (code: string) => {

      loading.value = true
      status.value = 'Exchanging code…'

      try {

        const tokens = await exchangeCodeForTokens(tenant.value, code, `${origin}/login2`)
        const claims = jwt.decode(tokens.id_token) as any

        // eslint-disable-next-line no-console
        console.log('[session-transfer-poc] code-exchange id_token', claims)

        const claim = (name: string) => claims?.[`https://siteminder.com/${name}`]

        readout.value = [
          { label: 'sid (Auth0 session id)', value: claims?.sid || 'MISSING', good: !!claims?.sid },
          { label: 'email', value: claims?.email || 'MISSING', good: !!claims?.email },
          { label: 'email_verified', value: String(claims?.email_verified), good: !!claims?.email_verified },
          { label: 'claim: session', value: claim('session') ? 'present' : 'MISSING', good: !!claim('session') },
          { label: 'claim: authenticationMethods', value: claim('authenticationMethods') ? 'present' : 'MISSING', good: !!claim('authenticationMethods') },
          { label: 'claim: riskAssessment', value: claim('riskAssessment') ? 'present' : 'MISSING', good: !!claim('riskAssessment') },
          { label: 'claim: geoip', value: claim('geoip') ? 'present' : 'MISSING', good: !!claim('geoip') },
        ]

        // What does Auth0 call this authentication? This drives passkeySession detection
        // in auth-api (`authMethods[0].name === 'passkey'`).
        authMethods.value = JSON.stringify(claim('authenticationMethods') ?? null)

        // The question the whole exercise exists to answer: does the browser now hold a
        // usable Auth0 session? getUserToken() is the exact call enrolment depends on.
        status.value = 'Checking the Auth0 cookie via getUserToken()…'
        cookieWorks.value = await getUserToken(tenant.value).then(() => true).catch(() => false)

        status.value = 'Done'

      } catch (error: any) {

        errorMessage.value = error?.response?.data?.error_description
          || error?.message
          || 'Code exchange failed'

        // eslint-disable-next-line no-console
        console.error('[session-transfer-poc] code exchange failed', error, error?.response?.data)

      } finally {
        loading.value = false
      }

    }

    onMounted(() => {
      const code = new URLSearchParams(window.location.search).get('code')
      if (code) {
        inspectReturn(code)
      }
    })

    return {
      tenant,
      logo,
      loading,
      errorMessage,
      status,
      readout,
      authMethods,
      cookieWorks,
      runFlow,
      runPasskeyOnly,
    }

  },

})

</script>

<template>
  <default-layout
    class="login2"
    title="Passkey login → session transfer"
    sub-title="Native Passkeys + Native-to-Web SSO POC"
    :logo-src="logo"
    logo-alt="SiteMinder"
  >
    <sm-form id="login2-form">
      <fieldset>
        <p class="hint">
          rpId comes from the tenant custom domain's <code>relying_party_identifier</code>.
          The transfer token is single-use and lasts ~60s, so the page navigates immediately.
        </p>

        <sm-button
          id="login2-inspect"
          type="primary"
          :loading="loading"
          @click="runFlow('inspect')"
        >
          Run flow → return here and inspect
        </sm-button>

        <sm-button
          id="login2-real"
          type="secondary"
          :loading="loading"
          @click="runFlow('real')"
        >
          Run flow → real login (/authenticate)
        </sm-button>

        <sm-button
          id="login2-grant-only"
          type="tertiary"
          :loading="loading"
          @click="runPasskeyOnly"
        >
          Diagnostic: webauthn grant only (no transfer, no redirect)
        </sm-button>

        <p
          v-if="status"
          class="message"
        >
          {{ status }}
        </p>

        <p
          v-if="errorMessage"
          class="message message--error"
        >
          {{ errorMessage }}
        </p>

        <div
          v-if="readout"
          class="readout"
        >
          <h6>id_token from the code exchange</h6>
          <div
            v-for="row in readout"
            :key="row.label"
            class="readout__row"
          >
            <span>{{ row.label }}</span>
            <span :class="row.good ? 'ok' : 'bad'">{{ row.value }}</span>
          </div>

          <div class="readout__row">
            <span>authenticationMethods</span>
            <span class="raw">{{ authMethods }}</span>
          </div>

          <div
            v-if="cookieWorks !== null"
            class="readout__row"
          >
            <span>Auth0 cookie usable (getUserToken)</span>
            <span :class="cookieWorks ? 'ok' : 'bad'">{{ cookieWorks ? 'YES' : 'NO' }}</span>
          </div>
        </div>
      </fieldset>
    </sm-form>
  </default-layout>
</template>

<style lang="scss" scoped>
#login2-inspect,
#login2-real,
#login2-grant-only {
  @apply w-full mb-2.5;
}

:deep(.sm-button__content) {
  @apply justify-center;
}

:deep(.sm-form) {
  @apply flex justify-center flex-col;
  fieldset {
    @apply self-center sm:w-[460px] max-w-[460px];
    min-width: inherit;
  }
}

.hint {
  @apply mb-4 text-center text-xs text-gray-500;
}

.message {
  @apply mt-2.5 text-center;

  &--error {
    @apply text-red-600;
  }
}

.readout {
  @apply mt-5 text-xs border-t pt-3;

  h6 {
    @apply mb-2;
  }

  &__row {
    @apply flex justify-between gap-4 py-1 border-b border-gray-100;
  }
}

.ok {
  @apply text-green-600 font-semibold;
}

.bad {
  @apply text-red-600 font-semibold;
}

.raw {
  @apply text-gray-600 break-all text-right;
}
</style>
